import { classNames } from '@/utils/class-names';
import React, { forwardRef, ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import LoadingSpin from './LoadingSpin';
import { Link } from 'react-router-dom';

import './index.css';

type ButtonProps = {
  children: ReactNode;
  as?: 'button' | 'a';
  onClick?: (e?: React.MouseEvent) => void;
  disabled?: boolean;
  href?: string;
  theme?: 'outline' | 'outline-accent' | 'solid' | 'solid-white' | 'solid-primary-600';
  size?: 'default' | 'lg';
  className?: string;
  type?: 'submit' | 'button';
  loading?: boolean;
  showChevron?: boolean;
  id?: string;
};

type ButtonElementProps = ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorElementProps = ButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;

type ButtonLinkProps = ButtonElementProps | AnchorElementProps;

const getTheme = (theme: 'outline' | 'outline-accent' | 'solid' | 'solid-white' | 'solid-primary-600' = 'solid') => {
  const defaultCss =
    'font-normal font-haffersq text-sh-16-24 tracking-[0.05em] text-center disabled:bg-white disabled:border disabled:border-sh-grey-eee disabled:text-sh-grey-500 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-sh-grey-eee disabled:hover:text-sh-grey-500 disabled:focus:bg-white disabled:focus:border-sh-grey-eee disabled:focus:text-sh-grey-500 disabled:focus:outline-none';

  switch (theme) {
    case 'outline':
    case 'outline-accent':
      return `border border-[#F15D49] bg-[#FFFFFF] text-[#173737] font-bold hover:border-2 hover:border-people-blue-500 selected:border-people-green-200 focus:border-people-blue-500 focus:bg-white hover:bg-people-green-200 sh-button-outline ${defaultCss}`;
    case 'solid':
      return `border-2 border-transparent selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-people-teal-600 text-white sh-button-solid hover:!text-[#173737] hover:!border-people-blue-500 selected:!border-people-green-200 focus:!border-people-blue-500 focus:!bg-white hover:!bg-people-green-200 focus:!text-[#173737] ${defaultCss}`;
    case 'solid-white':
      return `border-2 border-[#A1C3EC] hover:border-[#6AAEFE] selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-white text-primary-600 sh-button-solid ${defaultCss}`;
    case 'solid-primary-600':
      return `selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-primary-600 text-secondary-700 sh-button-solid ${defaultCss}`;
    default:
      return ``;
  }
};

const getSize = (size: 'default' | 'lg') => {
  if (size === 'lg') {
    return `rounded-2xl py-5 px-sh-30`;
  }

  return `rounded-[8px] py-[13px] px-[15px]`;
};

const CustomButton: React.ForwardRefRenderFunction<ButtonLinkProps, ButtonProps> = (
  {
    className = '',
    children,
    onClick,
    type = 'button',
    disabled,
    as,
    // size = 'md',
    theme = 'solid',
    loading,
    showChevron = true,
    href,
    size = 'default',
    ...rest
  },
  ref,
) => {
  const Element = as === 'a' ? Link : 'button';
  const linkProps = as === 'a' ? { to: href } : {};

  return (
    <Element
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      {...linkProps}
      {...rest}
      className={classNames(`sh-button`, getTheme(theme), getSize(size), className)}>
      <div className="sh-button flex items-center justify-center gap-2">
        {loading && <LoadingSpin />}
        {children}
        {theme === 'solid' && showChevron && !loading && (
          <ChevronRightIcon className="h-5 w-5 shrink-0 text-people-coral" aria-hidden />
        )}
      </div>
    </Element>
  );
};

export default forwardRef(CustomButton);
